import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { HelcimInvoiceListResponse, TestPaymentService } from '../services/test-payment.service';
import { PortalNavbarComponent } from './portal-navbar.component';

interface QuickLink {
  label: string;
  route: string;
  icon: string;
}

@Component({
  selector: 'app-payment-services-invoices-get-invoices-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Payment Services > Invoices > Get Invoices</h1>
          <p class="page-sub">Search and page through invoices for the selected organization.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="related-card related-card--top">
            <h3>Quick Access</h3>
            <div class="related-grid">
              @for (link of relatedLinks; track link.route) {
                <a class="quick-card" [routerLink]="link.route">
                  <div class="quick-card-hd">
                    <span class="quick-icon">{{ link.icon }}</span>
                    <span>{{ link.label }}</span>
                  </div>
                </a>
              }
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Get Invoices</span>
            </div>

            <div class="form">
              <label class="field">
                <span>Organization (Optional)</span>
                <select [(ngModel)]="organizationId" name="organizationId" [disabled]="isLoadingOrganizations || isLoading">
                  <option [ngValue]="0">{{ isLoadingOrganizations ? 'Loading organizations...' : 'Any scoped organization' }}</option>
                  @for (organization of organizations; track organization.id) {
                    <option [ngValue]="organization.id">{{ organization.displayName }} ({{ organization.legalName }})</option>
                  }
                </select>
              </label>
              <label class="field"><span>Page</span><input type="number" [(ngModel)]="page" name="page" min="1" /></label>
              <label class="field"><span>Date Start</span><input type="text" [(ngModel)]="dateStart" name="dateStart" placeholder="YYYY-MM-DD" /></label>
              <label class="field"><span>Date End</span><input type="text" [(ngModel)]="dateEnd" name="dateEnd" placeholder="YYYY-MM-DD" /></label>
              <label class="field"><span>Invoice Number</span><input type="text" [(ngModel)]="invoiceNumber" name="invoiceNumber" maxlength="64" /></label>
              <label class="field"><span>Customer Id</span><input type="text" [(ngModel)]="customerId" name="customerId" maxlength="64" /></label>
              <label class="field"><span>Status</span><input type="text" [(ngModel)]="status" name="status" maxlength="32" /></label>
              <label class="field"><span>Type</span><input type="text" [(ngModel)]="type" name="type" maxlength="32" /></label>
              <label class="field"><span>Currency</span><input type="text" [(ngModel)]="currency" name="currency" maxlength="3" /></label>
              <label class="field"><span>With Convenience Fee (0/1)</span><input type="number" [(ngModel)]="withConvenienceFee" name="withConvenienceFee" min="0" max="1" /></label>
              <label class="field"><span>With Order Fields (0/1)</span><input type="number" [(ngModel)]="withOrderFields" name="withOrderFields" min="0" max="1" /></label>
              <label class="field"><span>Convenience Fee Enabled (0/1)</span><input type="number" [(ngModel)]="convenienceFeeEnabled" name="convenienceFeeEnabled" min="0" max="1" /></label>
              <div class="actions">
                <button type="button" class="btn btn--primary" [disabled]="isLoading" (click)="getInvoices()">
                  {{ isLoading ? 'Loading...' : 'Get Invoices' }}
                </button>
              </div>
            </div>

            @if (errorMessage) {
              <div class="result result--error">{{ errorMessage }}</div>
            }

            <div class="grid-wrap">
              <div class="grid-title">Response Grid</div>
              <table class="result-grid">
                <thead>
                  <tr>
                    <th>Invoice Id</th>
                    <th>Invoice Number</th>
                    <th>Customer Id</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Currency</th>
                    <th>Date Issued</th>
                  </tr>
                </thead>
                <tbody>
                  @for (invoice of invoices; track invoice.invoiceId) {
                    <tr (dblclick)="openInvoice(invoice.invoiceId)">
                      <td>{{ invoice.invoiceId }}</td>
                      <td>{{ invoice.invoiceNumber || 'N/A' }}</td>
                      <td>{{ invoice.customerId || 'N/A' }}</td>
                      <td>{{ invoice.status || 'N/A' }}</td>
                      <td>{{ invoice.type || 'N/A' }}</td>
                      <td>{{ invoice.currency || 'N/A' }}</td>
                      <td>{{ invoice.dateIssued || 'N/A' }}</td>
                    </tr>
                  } @empty {
                    <tr>
                      <td colspan="7" class="empty-row">No invoices loaded yet.</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card,.related-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card{padding-bottom:1rem}.related-card{padding:1rem 1.1rem}.related-card--top{margin-bottom:1rem}.related-card h3{margin:0 0 .55rem}.related-grid{display:grid;gap:.8rem;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.quick-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent);padding:.85rem 1rem;text-decoration:none;color:var(--color-text);transition:transform .15s ease,border-color .15s ease}.quick-card:hover{transform:translateY(-2px);border-color:color-mix(in srgb,var(--color-accent) 38%,var(--color-border))}.quick-card-hd{display:flex;align-items:center;gap:.5rem}.quick-icon{display:inline-flex;align-items:center;justify-content:center;width:1.2rem;height:1.2rem}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-title{font-weight:600}.form{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:1rem}.field{display:flex;flex-direction:column;gap:.35rem}.field input,.field select{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field select{color-scheme:dark}.field select option{background:var(--color-bg-end);color:var(--color-text)}.actions{grid-column:1 / -1;display:flex;justify-content:flex-start}.btn{align-items:center;border:1px solid transparent;border-radius:.6rem;cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;gap:.35rem;line-height:1;padding:.6rem .9rem;transition:all .18s ease}.btn:disabled{cursor:not-allowed;opacity:.55}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.grid-wrap{padding:0 1rem}.grid-title{font-weight:600;margin-bottom:.5rem}.result-grid{border-collapse:collapse;width:100%}.result-grid th,.result-grid td{border:1px solid var(--color-border);padding:.5rem .55rem;text-align:left;vertical-align:top}.result-grid th{background:color-mix(in srgb,var(--color-surface-1) 75%,transparent)}.result-grid tbody tr:hover{background:color-mix(in srgb,var(--color-accent) 8%,transparent);cursor:pointer}.empty-row{color:var(--color-muted);text-align:center}.result{margin:0 1rem .9rem;padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}`]
})
export class PaymentServicesInvoicesGetInvoicesPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly router = inject(Router);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  organizationId = 0;
  page = 1;
  dateStart = '';
  dateEnd = '';
  invoiceNumber = '';
  customerId = '';
  status = '';
  type = '';
  currency = '';
  withConvenienceFee: 0 | 1 | null = null;
  withOrderFields: 0 | 1 | null = null;
  convenienceFeeEnabled: 0 | 1 | null = null;

  organizations: OrganizationDto[] = [];
  isLoadingOrganizations = false;
  isLoading = false;
  errorMessage = '';
  response: HelcimInvoiceListResponse | null = null;

  readonly relatedLinks: QuickLink[] = [
    { label: 'Get Invoice', route: '/payment-services/invoices/get-invoice', icon: '📄' }
  ];

  get invoices() {
    return this.response?.invoices ?? [];
  }

  async ngOnInit(): Promise<void> {
    await this.loadOrganizationsAsync();
  }

  private async loadOrganizationsAsync(): Promise<void> {
    this.isLoadingOrganizations = true;

    try {
      const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
      this.organizations = isSiteAdmin
        ? await firstValueFrom(this.organizationService.getAll())
        : [await firstValueFrom(this.organizationService.getMyOrganization())];

      if (this.organizationId <= 0 && this.organizations.length === 1) {
        this.organizationId = this.organizations[0].id;
      }
    } catch {
      this.organizations = [];
    } finally {
      this.isLoadingOrganizations = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async getInvoices(): Promise<void> {
    this.errorMessage = '';

    this.isLoading = true;

    try {
      const result = await this.testPaymentService.getInvoices({
        organizationId: this.organizationId > 0 ? this.organizationId : undefined,
        page: this.page,
        dateStart: this.dateStart.trim() || undefined,
        dateEnd: this.dateEnd.trim() || undefined,
        invoiceNumber: this.invoiceNumber.trim() || undefined,
        customerId: this.customerId.trim() || undefined,
        status: this.status.trim() || undefined,
        type: this.type.trim() || undefined,
        currency: this.currency.trim() || undefined,
        withConvenienceFee: this.withConvenienceFee ?? undefined,
        withOrderFields: this.withOrderFields ?? undefined,
        convenienceFeeEnabled: this.convenienceFeeEnabled ?? undefined
      }).toPromise();

      if (!result) {
        throw new Error('Get Invoices request returned no data.');
      }

      this.response = result;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Get Invoices request failed.';
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  openInvoice(invoiceId: string): void {
    if (!invoiceId) {
      return;
    }

    if (this.organizationId > 0) {
      void this.router.navigate(['/payment-services/invoices/get-invoice'], {
        queryParams: {
          organizationId: this.organizationId,
          invoiceId
        }
      });
      return;
    }

    void this.router.navigate(['/payment-services/invoices/get-invoice'], {
      queryParams: { invoiceId }
    });
  }
}
